import { FetchResult } from "@apollo/client";
import {
  Form,
  Input,
  message,
  Modal,
  InputNumber,
  Alert,
  Checkbox,
  Spin,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";

import {
  NewAdjustmentMutation,
  RollFormat,
  useNewAdjustmentMutation,
  useWeekRollSessionsLazyQuery,
  Week,
  WeekRollFieldsFragment,
} from "../../../../generated/graphql";
import {
  formatDuration,
  formatSession,
} from "../../../components/AttendanceTable/components/Roll/rollUtils";

const { Text } = Typography;

const SessionOption = styled.div`
  margin-bottom: 6px;
`;

const Hours = styled.div`
  margin-top: 8px;
`;

/** Sessions the class was taught in the week, which are the ones the teacher is paid for. */
const getTaughtSessions = (weekRoll?: WeekRollFieldsFragment) =>
  weekRoll?.format === RollFormat.Sessions
    ? weekRoll.sessions.filter((session) =>
        weekRoll.entries.some(
          (entry) => entry.sessionKey === session.key && entry.isPresent
        )
      )
    : [];

const NewAdjustmentModal: React.FC<{
  teacherClassLinkId?: number;
  classId?: number;
  week: Week;
  onSuccessfulCreate: (klass: FetchResult<NewAdjustmentMutation>) => void;
  onCancel: () => void;
}> = ({ teacherClassLinkId, classId, week, onSuccessfulCreate, onCancel }) => {
  const [newAdjustment, { loading }] = useNewAdjustmentMutation();
  const [getWeekRoll, weekRollResult] = useWeekRollSessionsLazyQuery({
    fetchPolicy: "network-only",
  });
  const [form] = Form.useForm();
  const [excludedKeys, setExcludedKeys] = useState<string[]>([]);
  const [error, setError] = useState<string>();

  const isVisible = teacherClassLinkId !== undefined;

  useEffect(() => {
    if (isVisible && classId) {
      getWeekRoll({ variables: { classId, week } });
    }
    if (!isVisible) {
      setExcludedKeys([]);
      setError(undefined);
    }
  }, [isVisible, classId, week]);

  const weekRoll = weekRollResult.data?.getClasses[0]?.weekRolls[0];
  const taughtSessions = getTaughtSessions(weekRoll);
  // Weeks still in the old format have no sessions to choose from, so the hours are entered.
  const isBySession = taughtSessions.length > 0;
  const excludedMinutes = taughtSessions
    .filter((session) => excludedKeys.includes(session.key))
    .reduce((total, session) => total + session.durationMinutes, 0);

  return (
    <Modal
      visible={isVisible}
      title="New Adjustment"
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      okButtonProps={{ disabled: weekRollResult.loading }}
      onCancel={onCancel}
      onOk={async (): Promise<void> => {
        try {
          if (!teacherClassLinkId) {
            return;
          }

          if (isBySession && excludedKeys.length === 0) {
            setError("Please select the sessions you did not teach.");
            return;
          }
          if (isBySession && excludedKeys.length === taughtSessions.length) {
            setError(
              "You cannot leave out every session. Untick the class for this week on the Attendance page instead."
            );
            return;
          }
          setError(undefined);

          const values = await form.validateFields();
          const input = isBySession
            ? {
                excludedSessionKeys: excludedKeys,
                reason: values.reason,
                teacherClassLinkId,
                week,
              }
            : {
                hoursToSubtract: values.hoursToSubtract,
                oldAttendanceData: values.datesAndTimesTaught,
                reason: values.reason,
                teacherClassLinkId,
                week,
              };

          const adjustment = await newAdjustment({
            variables: { input },
          });
          onSuccessfulCreate(adjustment);

          form.resetFields();
          setExcludedKeys([]);
          message.success("Successfully created adjustment");
        } catch (error) {
          message.error(
            `Server failed to create adjustment: ${error.message}`,
            10
          );
        }
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item>
          <Alert
            message={
              isBySession ? (
                <>
                  Leave out the sessions you did not teach this week. Your pay
                  for the ones you did teach is worked out from their dates and
                  times.
                  <br />
                  <br /> (When we say &apos;teach&apos; this also includes
                  assisting, training etc.)
                </>
              ) : (
                <>
                  The selected timesheet item should be adjusted if and only if:
                  <br />
                  <br />➤ You didn&apos;t teach a class for the entire duration
                  (e.g. 1hr instead of 2hrs) OR
                  <br />➤ Multiple lessons occurred in the week and you
                  didn&apos;t teach all of them.
                  <br />
                  <br /> (When we say &apos;teach&apos; this also includes
                  assisting, training etc.)
                </>
              )
            }
            type="warning"
            showIcon
          />
        </Form.Item>
        {weekRollResult.loading ? (
          <Spin />
        ) : isBySession ? (
          <Form.Item
            label="Sessions you did NOT teach"
            validateStatus={error ? "error" : undefined}
            help={error}
            required
          >
            <Checkbox.Group
              value={excludedKeys}
              onChange={(keys) => {
                setExcludedKeys(keys as string[]);
                setError(undefined);
              }}
            >
              {taughtSessions.map((session) => (
                <SessionOption key={session.key}>
                  <Checkbox value={session.key}>
                    {formatSession(session)}{" "}
                    <Text type="secondary">
                      ({formatDuration(session.durationMinutes)})
                    </Text>
                  </Checkbox>
                </SessionOption>
              ))}
            </Checkbox.Group>
            <Hours>
              <Text type="secondary">
                {excludedMinutes > 0
                  ? `${formatDuration(excludedMinutes)} will be taken off.`
                  : "Nothing will be taken off yet."}
              </Text>
            </Hours>
          </Form.Item>
        ) : (
          <>
            <Form.Item
              name="hoursToSubtract"
              label="Hours to subtract"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber
                placeholder="Any real number up to 2 d.p. will suffice."
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              name="datesAndTimesTaught"
              label="ALL Dates AND times YOU taught the class this week"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input placeholder="Be careful and input this accurately!" />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="reason"
          label="Reason"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input placeholder="Reason for adjusting timesheet." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewAdjustmentModal;
